import React from 'react';
import styled from 'styled-components/macro';
import { Manager, Reference, Popper } from 'react-popper';

import { ArrowDownIcon } from 'icons/ArrowDownIcon';
import { ClickOutside } from 'jsx/layout/ClickOutside';
import { Checkbox } from './Checkbox';
import { DatePicker } from './DatePicker';

export const Select = React.memo(props => {
  const {
    disabled,
    variant = 'select',
    items,
    getDisplayTitle,
    title: displayTitle,
    defaultValue = null,
    getItemDisplay,
    onChange
  } = props;
  const [open, setOpen] = React.useState(false);

  const computeSelected = () => {
    if (typeof defaultValue === 'string' || typeof defaultValue === 'number') {
      return { [defaultValue]: true };
    }
    if (defaultValue === null) {
      if (variant === 'date-multi') return { from: '', to: '' };
    }
    return defaultValue || {};
  };
  const [selected, setSelected] = React.useState(computeSelected);

  React.useEffect(() => {
    if (defaultValue) {
      if (
        typeof defaultValue === 'object' &&
        Object.keys(defaultValue) === 0 &&
        Object.keys(selected) === 0
      )
        return;
      setSelected(computeSelected);
    }
  }, [defaultValue]);

  const containerRef = React.useRef();

  const onSelect = (val, field) => {
    if (disabled) return;

    if (variant === 'date-multi') {
      setSelected(old => {
        const newObj = Object.assign({}, old);
        newObj[field] = val;
        return newObj;
      });
      return;
    }

    if (variant === 'select') {
      setOpen(false);
      setSelected({ [val]: true });
      onChange(val);
      return;
    }

    if (variant === 'multi') {
      setSelected(old => {
        const newVal = Object.assign({}, old);
        newVal[val] = !newVal[val];
        onChange(newVal);
        return newVal;
      });
      return;
    }
  };

  const title = React.useMemo(() => {
    let title = displayTitle || '';
    if (variant === 'date-multi') return title;
    const selectedKeys = Object.keys(selected).filter(key => Boolean(selected[key]));
    if (selectedKeys.length > 0) {
      title = selectedKeys
        .map(k => {
          return items.find(it => String(it.value) === String(k)).display;
        })
        .join(', ');

      if (getDisplayTitle) {
        return getDisplayTitle(title);
      }
    }

    return title;
  }, [displayTitle, getDisplayTitle, selected, variant]);

  return (
    <ClickOutside
      onOutsideClick={e => {
        if (disabled) return;
        if (open) {
          e.stopPropagation();
          e.preventDefault();
          setOpen(false);
        }
      }}
    >
      <Manager>
        <Reference>
          {({ ref }) => (
            <div ref={ref}>
              <SelectContainer
                disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                open={open}
                ref={containerRef}
                onClick={() => {
                  if (disabled) return;
                  setOpen(old => !old);
                }}
              >
                <SelectText>{title}</SelectText>
                <StyledArrow open={open} />
              </SelectContainer>
            </div>
          )}
        </Reference>
        {open && (
          <Popper placement="bottom-start">
            {({ ref, style, placement, arrowProps }) => {
              const parentWidth = containerRef.current.getBoundingClientRect().width;
              console.log('p', parentWidth, containerRef);
              return (
                <OptionsContainer
                  ref={ref}
                  style={style}
                  data-placement={placement}
                  width={parentWidth}
                  variant={variant}
                >
                  <RenderSelectVariants
                    variant={variant}
                    items={items}
                    onSelect={onSelect}
                    selected={selected}
                    getItemDisplay={getItemDisplay}
                  />
                  <div ref={arrowProps.ref} style={arrowProps.style} />
                </OptionsContainer>
              );
            }}
          </Popper>
        )}
      </Manager>
    </ClickOutside>
  );
});

const RenderSelectVariants = React.memo(props => {
  const { variant, items, onSelect, selected, getItemDisplay } = props;
  switch (variant) {
    case 'date-multi':
      return (
        <div>
          <div>
            <DateLabel>Frá:</DateLabel>
            <DatePicker
              value={selected.from}
              onChange={val => onSelect(val, 'from')}
              maxDate={selected.to}
            />
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <DateLabel>Til:</DateLabel>
            <DatePicker
              value={selected.to}
              onChange={val => onSelect(val, 'to')}
              minDate={selected.from}
            />
          </div>
        </div>
      );
    case 'select':
      return (
        <>
          {items.map(item => (
            <Option
              active={Boolean(selected[item.value])}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(item.value);
              }}
              key={item.value}
            >
              {item.display}
            </Option>
          ))}
        </>
      );
    case 'multi':
      return (
        <>
          {items.map(item => {
            const checked = Boolean(selected[item.value]);
            const display = getItemDisplay ? getItemDisplay(item.display) : item.display;
            return (
              <Option
                active={checked}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect(item.value);
                }}
                key={item.value}
              >
                <Checkbox checked={checked} label={display} />
              </Option>
            );
          })}
        </>
      );
    default:
      return null;
  }
});

const SelectText = styled.div({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap'
});

const SelectContainer = styled.div(p => {
  const hoverStyle = {
    border: '0.0625rem solid #4091C6'
  };

  return {
    border: '0.0625rem solid #bfbfbf',
    background: '#fff',
    padding: '0 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    minHeight: '2.5rem',
    color: '#333333',
    outline: 'none',
    ...(p.disabled && { opacity: 0.5 }),
    ...(!p.disabled && {
      cursor: 'pointer',
      ':hover, :focus': hoverStyle,
      ...(p.open && hoverStyle)
    })
  };
});

const DateLabel = styled.div({
  fontSize: '0.875rem',
  color: '#666'
});

const OptionsContainer = styled.div(p => ({
  background: '#F3F3F3',
  boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.25)',
  minWidth: p.width - 36,
  maxWidth: '16rem',
  padding: '0.75rem 1rem',
  zIndex: 2,
  ...(p.variant !== 'date-multi' && {
    maxHeight: '17rem',
    overflowY: 'auto'
  })
}));

const Option = styled.div(p => ({
  fontSize: '0.875rem',
  color: '#333333',
  width: '100%',
  ':hover': {
    fontWeight: 600,
    cursor: 'pointer'
  },
  ...(p.active && { fontWeight: 600 }),
  minHeight: '2rem',
  display: 'flex',
  alignItems: 'center'
}));

const StyledArrow = styled(ArrowDownIcon)(p => ({
  flexShrink: 0,
  transition: 'transform 150ms linear',
  ...(p.open && {
    transform: 'rotate(180deg)'
  }),
  marginLeft: '0.75rem',
  zIndex: 0
}));
